import { ButtonSecondary, cssColorValue, screenMdMin, semanticColors, spacer12 } from '@spotify-internal/encore-web';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useProperty } from '@spotify-internal/remote-config-integration-react';

import { getConsumerSecondaryLinks } from 'components/Masthead/shared/utils';
import { MastheadLink, MastheadLinkList, MastheadLinkOrLinkListArray } from 'lib/sanity/types';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';
import { urlForImage } from 'utils/sanity';
import { NavBarLanguagePicker } from 'components/Masthead/Header/LanguagePicker';
import { NavBarProfileLinksPopover } from 'components/Masthead/Header/Popover/ProfileLinks';
import { isMastheadLinkList } from 'utils/type-guards';
import { MOBILE_NAVIGATION_BREAKPOINT } from 'components/Masthead/Header/types';
import useDropdown, { DropdownContext } from 'components/Masthead/Header/hooks/useDropdown';
import { NavBarSecondaryLinksPopover } from 'components/Masthead/Header/Popover/SecondaryLinks';
import { Vertical } from 'utils/vertical';
import { enablePremiumDropdown } from 'utils/remote-config/properties';

const LinksWrapper = styled.section`
  & > ul {
    align-items: center;
    display: flex;
    gap: ${spacer12};
    list-style: none;
    margin: 0;
    padding: 0;
  }

  @media (max-width: ${screenMdMin}) {
    display: none;
  }
`;

const PrimaryButton = styled(ButtonSecondary)`
  background-color: ${cssColorValue(semanticColors.textBase)};
  border: 1px solid ${cssColorValue(semanticColors.textBase)};
  color: ${cssColorValue(semanticColors.backgroundBase)};

  &:hover {
    border: 1px solid ${cssColorValue(semanticColors.textBase)};
  }
`;

const SecondaryButton = styled(ButtonSecondary)`
  background: ${cssColorValue(semanticColors.backgroundBase)};
  border: 1px solid ${cssColorValue(semanticColors.backgroundBase)};
  color: ${cssColorValue(semanticColors.textBase)};

  &:hover {
    background: ${cssColorValue(semanticColors.backgroundBase)};
  }
`;

const OutlineButton = styled(ButtonSecondary)`
  background: transparent;
  border: 1px solid ${cssColorValue(semanticColors.textBase)};
  color: ${cssColorValue(semanticColors.textBase)};

  &:hover {
    background: transparent;
  }
`;

const IconLeadingImage = styled.img<{
  imgUrl: string;
}>`
  background-color: ${cssColorValue(semanticColors.textBase)};
  mask: ${({ imgUrl }) => `url(${imgUrl}) no-repeat center`};
  height: 16px;
  width: 16px;
`;

const SecondaryLinks = ({
  secondaryLinks,
  showHeaderGradient,
}: {
  secondaryLinks: Array<(MastheadLinkList | MastheadLink) & { iconUrl?: string }>;
  showHeaderGradient?: boolean;
}) => {
  const getSecondaryLinkComponent = (variant?: MastheadLink['variant']) => {
    if (variant === 'primaryButton') return PrimaryButton;
    if (showHeaderGradient) return SecondaryButton;
    return OutlineButton;
  };

  return secondaryLinks.map(({ iconUrl, ...item }) => {
    const LinkComponent = getSecondaryLinkComponent(item.variant);

    return (
      <li key={item._key}>
        {isMastheadLinkList(item) ? (
          <NavBarSecondaryLinksPopover linkListEntry={item}>
            {({ label, onMouseDown, onKeyDown }) => (
              <LinkComponent size="small" onMouseDown={onMouseDown} onKeyDown={onKeyDown}>
                {label}
              </LinkComponent>
            )}
          </NavBarSecondaryLinksPopover>
        ) : (
          <LinkComponent
            iconLeading={iconUrl ? () => <IconLeadingImage imgUrl={iconUrl} alt="" /> : undefined}
            size="small"
            href={item.href}
            target={item.target}
          >
            {item.title}
          </LinkComponent>
        )}
      </li>
    );
  });
};

type Props = {
  secondaryLinks?: MastheadLinkOrLinkListArray;
  profileLinks?: MastheadLinkOrLinkListArray;
};

const NavLinks = ({ profileLinks, secondaryLinks = [] }: Props) => {
  const { handlers, open } = useDropdown('secondary-nav');
  const {
    verticalConfig: { features, verticalName },
  } = useServerSideConstants();
  const showPremiumDropdown = useProperty(enablePremiumDropdown);

  const { showHeaderGradient } = features;

  const secondaryLinksArray = getConsumerSecondaryLinks(
    secondaryLinks,
    verticalName === Vertical.CONSUMER,
    showPremiumDropdown,
  ).reduce<Array<(MastheadLinkList | MastheadLink) & { iconUrl?: string }>>((acc, item) => {
    if (item.variant === 'primaryButton' || item.variant === 'secondaryButton') {
      const iconUrl = item.image && urlForImage({ source: item.image.asset, maxWidth: 16, vertical: verticalName });
      acc.push({ ...item, iconUrl });
    }
    return acc;
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= MOBILE_NAVIGATION_BREAKPOINT) {
        handlers.handleClose();
      }
    };

    window.addEventListener('resize', handleResize, false);
  }, [handlers, open]);

  return (
    <>
      {secondaryLinksArray && (
        <SecondaryLinks secondaryLinks={secondaryLinksArray} showHeaderGradient={showHeaderGradient} />
      )}
      {profileLinks && <NavBarProfileLinksPopover profileLinks={profileLinks} />}
    </>
  );
};

export const NavBarSecondaryLinks = (props: Props) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  return (
    <LinksWrapper>
      <ul>
        <li>
          <NavBarLanguagePicker container="header" />
        </li>
        <DropdownContext.Provider value={{ activeDropdown, setActiveDropdown }}>
          <NavLinks {...props} />
        </DropdownContext.Provider>
      </ul>
    </LinksWrapper>
  );
};
